import { useEffect, useState } from "react";
import { Ticket, FileArchive, UserPlus, Search, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { markReleaseNotesSeen, wasReleaseNotesSeen } from "@/lib/releaseNotes";

type ReleaseNote = {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

const notes: ReleaseNote[] = [
  {
    id: "tickets-peajes",
    title: "Tickets y peajes",
    description: "Mayor tolerancia para comprobantes de pago, tránsitos de autopista y tiques de aparcamiento.",
    icon: Ticket,
  },
  {
    id: "descarga-no-procesadas",
    title: "Descarga de no procesadas",
    description: "Botón para descargar en un archivo ZIP (o individualmente) todos los archivos que hayan quedado en la pestaña de rechazadas.",
    icon: FileArchive,
  },
  {
    id: "quick-supplier",
    title: "Alta rápida de proveedor (Ctrl + M)",
    description: "Desde la ficha de una factura, pulsa Ctrl + M (o Alt + N) para dar de alta al proveedor en el maestro sin salir de la pantalla.",
    icon: UserPlus,
  },
  {
    id: "global-search",
    title: "Búsqueda global (Ctrl + K)",
    description: "Pulsa Ctrl + K para buscar cualquier documento por proveedor o cliente, NIF, número o importe.",
    icon: Search,
  },
];

interface ReleaseNotesBannerProps {
  className?: string;
}

const ReleaseNotesBanner = ({ className }: ReleaseNotesBannerProps) => {
  const [wasSeen, setWasSeen] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    setWasSeen(wasReleaseNotesSeen());
  }, []);

  if (wasSeen || dismissed) return null;

  const handleClose = () => {
    if (dontShowAgain) {
      markReleaseNotesSeen();
    }
    setDismissed(true);
  };

  return (
    <Card
      role="region"
      aria-label="Novedades"
      className={cn(
        "relative border-brand/20 bg-brand/5 shadow-sm overflow-hidden",
        className,
      )}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-brand" />
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-brand/30 text-brand bg-brand/10">
                <Sparkles className="h-3 w-3 mr-1" />
                Novedades
              </Badge>
            </div>
            <CardTitle className="text-xl font-semibold font-[Playfair_Display,serif]">
              Novedades de esta versión
            </CardTitle>
            <CardDescription>
              Hemos añadido nuevas funciones para agilizar el procesamiento de documentos.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 -mr-2 -mt-2 text-muted-foreground hover:text-foreground"
            onClick={handleClose}
            aria-label="Cerrar novedades"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {notes.map((note) => {
            const Icon = note.icon;
            return (
              <div
                key={note.id}
                className="flex items-start gap-3 rounded-lg border bg-background/60 p-3"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold leading-tight">{note.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    {note.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-4 w-4 rounded border-input text-brand focus:ring-brand"
            />
            No volver a mostrar
          </label>
          <Button onClick={handleClose} className="bg-brand hover:bg-brand/90 text-brand-foreground">
            Entendido
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReleaseNotesBanner;
